import AppHeader from '../components/AppHeader';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import { ArrowBack, Delete, Add, VpnKey } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // User Management
  const [users, setUsers] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'technician'
  });

  // Password Change
  const [passwords, setPasswords] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Email Configuration
  const [emailConfig, setEmailConfig] = useState({
    angebot_recipient_email: ''
  });

  useEffect(() => {
    // Check if user is admin
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadUsers();
    loadEmailConfig();
  }, [user, navigate]);

  const loadEmailConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/settings/angebot-email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.email) {
        setEmailConfig({ angebot_recipient_email: response.data.email });
      }
    } catch (err) {
      console.error('Error loading email config:', err);
      // Silently fail, use default empty value
    }
  };

  const handleSaveEmailConfig = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    if (!emailConfig.angebot_recipient_email || !emailConfig.angebot_recipient_email.includes('@')) {
      setError('Bitte geben Sie eine gültige Email-Adresse ein');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/settings/angebot-email`,
        { email: emailConfig.angebot_recipient_email },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Email-Konfiguration erfolgreich gespeichert');
    } catch (err) {
      console.error('Error saving email config:', err);
      setError('Fehler beim Speichern der Email-Konfiguration');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const result = await userAPI.list();
      if (result.success) {
        setUsers(result.users);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Fehler beim Laden der Benutzer');
    }
  };

  const handleCreateUser = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!newUser.username || !newUser.password) {
      setError('Benutzername und Passwort sind erforderlich');
      setLoading(false);
      return;
    }

    if (newUser.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      setLoading(false);
      return;
    }

    try {
      const result = await userAPI.create(newUser);
      if (result.success) {
        setSuccess(`Benutzer ${newUser.username} erfolgreich erstellt`);
        setShowCreateDialog(false);
        setNewUser({
          username: '',
          password: '',
          email: '',
          full_name: '',
          role: 'technician'
        });
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Erstellen des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Möchten Sie den Benutzer "${username}" wirklich löschen?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await userAPI.delete(username);
      if (result.success) {
        setSuccess(`Benutzer ${username} erfolgreich gelöscht`);
        loadUsers();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen des Benutzers');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!passwords.old_password || !passwords.new_password || !passwords.confirm_password) {
      setError('Alle Felder sind erforderlich');
      setLoading(false);
      return;
    }

    if (passwords.new_password !== passwords.confirm_password) {
      setError('Neue Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    if (passwords.new_password.length < 6) {
      setError('Neues Passwort muss mindestens 6 Zeichen lang sein');
      setLoading(false);
      return;
    }

    try {
      const result = await userAPI.changePassword(passwords.old_password, passwords.new_password);
      if (result.success) {
        setSuccess('Passwort erfolgreich geändert');
        setPasswords({
          old_password: '',
          new_password: '',
          confirm_password: ''
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/dashboard')}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin-Bereich
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Benutzerverwaltung" />
            <Tab label="Passwort ändern" />
            <Tab label="Email-Konfiguration" />
          </Tabs>

          {/* User Management Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Benutzer</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowCreateDialog(true)}
              >
                Neuer Benutzer
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Benutzername</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>E-Mail</TableCell>
                    <TableCell>Rolle</TableCell>
                    <TableCell align="right">Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.username}>
                      <TableCell>{u.username}</TableCell>
                      <TableCell>{u.full_name || '-'}</TableCell>
                      <TableCell>{u.email || '-'}</TableCell>
                      <TableCell>
                        {u.role === 'admin' ? 'Administrator' : 'Techniker'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteUser(u.username)}
                          disabled={u.username === user?.username || loading}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {users.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Keine Benutzer gefunden</Typography>
              </Box>
            )}
          </TabPanel>

          {/* Password Change Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>Passwort ändern</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ändern Sie Ihr persönliches Passwort
            </Typography>

            <Box sx={{ maxWidth: 500 }}>
              <TextField
                fullWidth
                type="password"
                label="Altes Passwort"
                value={passwords.old_password}
                onChange={(e) => setPasswords({ ...passwords, old_password: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                type="password"
                label="Neues Passwort"
                value={passwords.new_password}
                onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                margin="normal"
                helperText="Mindestens 6 Zeichen"
              />
              <TextField
                fullWidth
                type="password"
                label="Neues Passwort bestätigen"
                value={passwords.confirm_password}
                onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                margin="normal"
              />
              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <VpnKey />}
                onClick={handleChangePassword}
                disabled={loading}
                sx={{ mt: 2 }}
                fullWidth
              >
                Passwort ändern
              </Button>
            </Box>
          </TabPanel>

          {/* Email Configuration Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography variant="h6" gutterBottom>Email-Konfiguration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Konfigurieren Sie die Empfänger-Email-Adresse für Angebote
            </Typography>

            <Box sx={{ maxWidth: 600 }}>
              <TextField
                fullWidth
                type="email"
                label="Empfänger Email für Angebote"
                value={emailConfig.angebot_recipient_email}
                onChange={(e) => setEmailConfig({ ...emailConfig, angebot_recipient_email: e.target.value })}
                margin="normal"
                helperText="Diese Email-Adresse erhält alle versendeten Angebote"
                placeholder="angebote@firma.de"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveEmailConfig}
                disabled={loading}
                sx={{ mt: 2 }}
                fullWidth
              >
                {loading ? <CircularProgress size={20} /> : 'Email-Konfiguration speichern'}
              </Button>
            </Box>
          </TabPanel>
        </Paper>
      </Container>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Benutzer erstellen</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Benutzername"
            value={newUser.username}
            onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            type="password"
            label="Passwort"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
            margin="normal"
            required
            helperText="Mindestens 6 Zeichen"
          />
          <TextField
            fullWidth
            label="Name"
            value={newUser.full_name}
            onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            type="email"
            label="E-Mail"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            margin="normal"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Rolle</InputLabel>
            <Select
              value={newUser.role}
              label="Rolle"
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <MenuItem value="technician">Techniker</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
          <Button 
            onClick={handleCreateUser} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
